/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

export class GanttItem {
    public label: string;
    public taskId: string;
    public startDate?: Date;
    public endDate?: Date;
    public rowPosition: number;
    public vSpan: number;  // The vertical area used to draw this object. This can be larger than the rowCount.
    public rowCount: number; // the vertical space allocated to just this object. Other object are pushed down.
    public duration?: string; // expressed in hours, weeks, days, etc. must show units. i.e.: '2w', '3d', '4h'
    public dependsOn: GanttItem[] = [];
    public type: 'entry' | 'sectionBackground' | 'sectionHeader'| 'programHeader' = 'entry';
    private styles: Map<string, string> = new Map<string, string>();
    // This field is used to position the text position, to move it out of the rectangle if it won't fit inside.
    public textLoc = 'Inside';

    constructor({
                    label,
                    taskId = label.replace(' ', '_'),
                    startDate = null,
                    endDate = null,
                    duration = '',
                    vSpan = 1,
                    rowCount = 1,
                    dependsOn = [],
                    type = 'entry'
                }: {
        label: string;
        taskId?: string;
        startDate?: Date;
        endDate?: Date;
        duration?: string;
        vSpan?: number;
        rowCount?: number;
        dependsOn?: GanttItem[];
        type?: 'entry' | 'sectionBackground' | 'sectionHeader'| 'programHeader';
    }) {
        this.label = label;
        this.taskId = taskId;
        this.startDate = startDate;
        this.endDate = endDate;
        this.duration = duration;
        this.vSpan = vSpan;
        this.rowCount = rowCount;
        this.dependsOn = dependsOn;
        this.type = type;
        if (!this.dependsOn && !this.startDate) {
            throw new Error('Either dependsOn or startDate must be provided');
        }
        if (!this.duration && !this.endDate) {
            throw new Error('Either duration or endDate must be provided');
        }
    }

    getDateStart(): Date {
        if (this.startDate) {
            return new Date(this.startDate);
        }
        let date;
        for (const dependency of this.dependsOn) {
            const dependencyDate = dependency.getDateEnd();
            if (!date || dependencyDate > date) {
                date = dependencyDate;
            }
        }
        return date;
    }
    getDateEnd(): Date {
        if (this.endDate) {
            return new Date(this.endDate);
        }
        // Figure the end date based on duration and start date
        const start = this.getDateStart();
        const units = this.duration.substring(this.duration.length - 1);
        const number = Number(this.duration.substring(0, this.duration.length - 1));
        if (start) {
            if (units === 'm') {
                return new Date(start.getTime() + number * 60 * 1000);
            }
            if (units === 'h') {
                return new Date(start.getTime() + number * 60 * 60 * 1000);
            }
            if (units === 'd') {
                return new Date(start.getTime() + number * 24 * 60 * 60 * 1000);
            }
            if (units === 'w') {
                return new Date(start.getTime() + number * 7 * 24 * 60 * 60 * 1000);
            }
        }
        return null;
    }

    includeDates(dateStart: Date, dateEnd: Date): void {
        if (this.getDateStart().getTime() > dateStart.getTime()) {
            this.startDate = dateStart;
        }
        if (this.getDateEnd().getTime() < dateEnd.getTime()) {
            this.endDate = dateEnd;
        }
    }

    setFillColor(color: string): void {
        this.styles.set('fill', color);
    }
    setOpacity(opacity: number): void {
        this.styles.set('opacity', '' + opacity);
    }
    setTextColor(color: string): void {
        this.styles.set('text', color);
    }
    getFillColor(): string {
        return this.styles.get('fill');
    }
    getOpacity(): string {
        return this.styles.get('opacity');
    }
    getTextColor(): string {
        return this.styles.get('text');
    }
    getTooltip(): string {
        return this.label + '\n' +
            'Start: ' + this.getDateStart()?.toLocaleDateString() + '\n' +
            'End: ' + this.getDateEnd()?.toLocaleDateString();
    }
}
